import { CheckIcon } from "@/components/ui/icon";
import { Checkbox, CheckboxIndicator, CheckboxIcon } from "@/components/ui/checkbox";
import { HStack } from "@/components/ui/hstack";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { Image } from "@/components/ui/image";
import React, { useState, useEffect } from 'react'
import { ModalWrapper } from "./ModalWrapper";

import { useSelector } from "react-redux";

import i18n from "i18n-js";

export const ContactLinkedPreference = (modal) => {
  const isWaiting = modal.option?.request?.isWaiting ?? false;
  const isAcknowledged = modal.option?.request?.isAcknowledged ?? false;
  const hasIssues = modal.option?.request?.issues ?? {};

  if (isAcknowledged) {
    modal.onClose();
  }

  const all_workflows_lookup = useSelector((state) => state.workflows);
  const all_workflows_owned = Object.fromEntries(Object.entries(all_workflows_lookup).filter(([key, value]) => value.owned && !value.deleted));
  const all_workflows = Object.values(all_workflows_owned);
  const all_channels = useSelector((state) => state.contacts.channels);

  const contact_state = useSelector((state) => state.contacts.active) //
    .filter(_ => _.id === modal.option?.contact?.id)[0] ?? {};

  const [modal_title, setModalTitle] = useState();
  const [modal_caption, setModalCaption] = useState();
  const [input_linked, setInputLinked] = useState({});
  const [given_linked, setGivenLinked] = useState({});

  useEffect(() => {
    if (contact_state?.id) {
      switch (modal.type) {
        case 'contact:update-workflows': {
          const entries = {};

          for (const k in contact_state.workflows) {
            const local_workflow_id = contact_state.workflows[k];
            entries[local_workflow_id] = all_workflows_owned[local_workflow_id];
          }

          setModalTitle(i18n.t('contact.workflowsN', { num: contact_state.workflows.length }));
          setModalCaption(i18n.t('channel.addWorkflowsText'));
          setInputLinked({ workflows: { ...entries } });
          setGivenLinked({ workflows: { ...entries } });

          break;
        }
        case 'contact:update-channels': {
          const entries = {};
          const linked_channels = all_channels.filter((_) => {
            return _.contacts.includes(contact_state.id);
          });

          for (const k in linked_channels) {
            const channel_id = linked_channels[k].id;
            entries[channel_id] = linked_channels[k];
          }

          setModalTitle(i18n.t('contact.channelsN', { num: linked_channels.length }));
          setModalCaption(i18n.t('studio.contact.contactChannelsFor', { contact: contact_state.toUser.preferredName.trim() }));
          setInputLinked({ channels: { ...entries } });
          setGivenLinked({ channels: { ...entries } });

          break;
        }
      }
    }
  }, [contact_state]);

  useEffect(() => {
    if (contact_state?.id && Object.keys(input_linked).length >= 1) {
      switch (modal.type) {
        case 'contact:update-workflows': {
          setModalTitle(i18n.t('contact.workflowsN', { num: Object.keys(input_linked.workflows).length }));
          break;
        }
        case 'contact:update-channels': {
          setModalTitle(i18n.t('contact.channelsN', { num: Object.keys(input_linked.channels).length }));
          break;
        }
      }

    }
  }, [contact_state, input_linked]);

  const onBack = () => {
    modal.onClose();
  }

  const onSubmit = () => {
    const channels_to_add = !('channels' in input_linked)
      ? undefined
      : all_channels.filter((_) => {
        return (
          Object.keys(input_linked.channels).includes(_.id) &&
          !Object.keys(given_linked.channels).includes(_.id)
        )
      }).map((_) => (_.id));

    const channels_to_remove = !('channels' in input_linked)
      ? undefined
      : all_channels.filter((_) => {
        return (
          !Object.keys(input_linked.channels).includes(_.id) &&
          Object.keys(given_linked.channels).includes(_.id)
        )
      }).map((_) => (_.id));

    const updated_workflows = (!('workflows' in input_linked)
      ? contact_state.workflows
      : Object.keys(input_linked.workflows)
    )?.map((local_workflow_id) => (all_workflows_owned[local_workflow_id].workflowId));

    submitInputs({
      contact: {
        ...contact_state,

        workflows: updated_workflows ?? undefined,
        addChannels: channels_to_add ?? undefined,
        delChannels: channels_to_remove ?? undefined
      }
    });
  }

  const submitInputs = ({ contact }) => {
    const request = {
      isWaiting: false,
      isAcknowledged: false,
      issues: null
    };

    // ? values
    console.debug('[Modal]', 'submit', { request })

    // ? request
    request.isWaiting = true;

    const request_path = '/contact/' + contact.id;
    const request_method = 'PUT';

    modal.onUpdate({ request });
    modal.onRequest('UPDATE_CHANNEL_INLINE', request_path, {
      contactAlias: contact.contactAlias,
      planningAllowed: contact.planningAllowed,

      workflows: contact.workflows,
      addChannels: contact.addChannels,
      delChannels: contact.delChannels
    }, request_method).then((data) => {
      // ---
      console.debug('[Modal]', 'onRequest.then', !!data.result, data?.result)
      // ---

      if (data.result) {
        modal.onUpdate({
          request: {
            isAcknowledged: true,  // signal modal can close
            isWaiting: true, // keep locked
            issues: null,
          }
        });
      }

    })
  }

  return (
    <ModalWrapper
      onClose={modal.onClose}
      onBack={onBack}
      onSubmit={onSubmit}

      isVisible={modal.isVisible}
      isWaiting={isWaiting}

      title={modal_title}
      buttonBack={{ text: i18n.t('addContact.cancel') }}
      buttonSubmit={{ text: i18n.t('channel.continue') }}
    >
      <Text className="text-md text-secondary-600">{modal_caption}</Text>
      <VStack space="md" className="max-h-[320px] px-[1rem] mx-[-1rem] overflow-auto">
        {'workflows' in input_linked && all_workflows.map((entry) => (
          <Checkbox
            key={entry.id}
            defaultIsChecked={entry.id in input_linked.workflows}
            onChange={(value) => {
              value ? (input_linked.workflows[entry.id] = entry) : (delete input_linked.workflows[entry.id]);
              setInputLinked({ ...input_linked });
            }}
            size="md" isInvalid={false} isDisabled={false}
          >
            <CheckboxIndicator className="h-8 w-8">
              <CheckboxIcon as={CheckIcon} />
            </CheckboxIndicator>
            <HStack space="md" className="px-6 h-12 items-center flex-1">
              <VStack >
                <Text className="text-md text-secondary-600 font-[800]">{entry.title}</Text>
                <Text className="text-md text-secondary-600">{i18n.t('showWorkflowScreen.numberOfSteps', { steps: entry.steps.length })}</Text>
              </VStack>
            </HStack>
          </Checkbox>
        ))}

        {'channels' in input_linked && all_channels.map((entry) => (
          <Checkbox
            key={entry.id}
            defaultIsChecked={entry.id in input_linked.channels}
            onChange={(value) => {
              value ? (input_linked.channels[entry.id] = entry) : (delete input_linked.channels[entry.id]);
              setInputLinked({ ...input_linked });
            }}
            size="md" isInvalid={false} isDisabled={false}
          >
            <CheckboxIndicator className="h-8 w-8">
              <CheckboxIcon as={CheckIcon} />
            </CheckboxIndicator>
            <HStack space="md" className="px-6 h-12 items-center flex-1">
              <VStack >
                <Text className="text-md text-secondary-600 font-[800]">{entry.name}</Text>
              </VStack>
            </HStack>
          </Checkbox>
        ))}
      </VStack>
      {'request-failed' in hasIssues && (
        <VStack>
          <Text className="mt-2 text-xs text-error-600">{hasIssues['request-failed']}</Text>
        </VStack>
      )}
    </ModalWrapper>
  );
}
